/**
 * Command - UI layer for the Command prestige layer (M2)
 *
 * Pure logic lives in command-core.js; this module renders the Command tab
 * (stats strip, upgrade roster, Legends gallery), the promote modal, and the
 * Regional Charter region-pick modal, and wires the header marks chip + tab
 * reveal. Player-facing verb is always promote/promotion.
 */

import * as config from '../constants.js';
import { gameState, saveGameState } from './gameState.js';
import { addNotification } from './notifications.js';
import { updateTabVisibility, updateUI } from './game.js';
import { generateContractors, reserveContractorFromModal } from './contractor.js';
import { displayCharacter } from './character.js';
import { updatePathDisplay, updateGridDisplay, updateStartRaidButton, updateCurrentRaidInfo, getHighlightedWantedCells } from './raid.js';
import { isContractorReserved } from './raid-core.js';
import { getAvatarSeed } from './util.js';
import { renderLegendCard } from './legendCard.js';
import { registerSubTabs, applySubTabs } from './subTabs.js';
import { blackoutModifier } from './blackout-core.js';
import { selectBestContractorsForPathCore } from './contractor-core.js';
import { findPathBFS, insertFreeSpecialDetours } from './raidPath-core.js';
import { isAutoRaidOwned, isAutoRaidEnabled, setAutoRaidEnabled, autoRaidBlockedReason, plannedEnergyCost, isWaitingForEnergy } from './autoRaid.js';
import { isAutoFabricatorOwned, isAutoFabricatorEnabled, setAutoFabricatorEnabled, autoFabricatorBlockedReason, autoFabricatorStatus } from './autoFabricator.js';
import {
    calculateCommandMarks,
    canPromoteCore,
    promoteCharacterCore,
    purchaseUpgradeCore,
    getUpgradeRank,
    getUpgradeEffect,
    isCommandRevealed,
    ensureCommandState,
    availableRegionsForCharacter,
    resolvePromotedFlavor,
    requiredUpgradesMissing,
    commandUpgradeCost,
    disburseInventory,
} from './command-core.js';

const VIOLET = '#b18cff';
const VIOLET_DIM = '#6d4fa3';

// ---------------------------------------------------------------------------
// Header chip + tab reveal
// ---------------------------------------------------------------------------

let wasRevealed = false;

/**
 * Handles the one-way Command reveal.
 *
 * Showing the tab and the marks chip is no longer done here: Command is a layer
 * registry entry whose `unlocked()` is this same reveal latch, and the toolbar
 * renders from that. What remains is the part the registry cannot express — the
 * one-time announcement and character-card re-render on first reveal.
 */
export function updateCommandHeader() {
    const revealed = isCommandRevealed();

    // First reveal mid-session: the character card was rendered without the
    // promote button - re-render it and announce the new layer once
    if (revealed && !wasRevealed) {
        wasRevealed = true;
        if (gameState.character) {
            displayCharacter();
        }
        if (!gameState.command.legends.length && !gameState.command.marks) {
            // Sticky: a one-way latch that fires on a background tick rather than
            // on anything the player just did, and it opens a whole layer. Missing
            // it means not knowing the Command tab appeared.
            addNotification('🎖️ Faction command has taken notice - the Command layer is now available', 'success', { sticky: true });
            saveGameState();
        }
        // A newly unlocked layer changes what the switcher can reach.
        window.renderLayerToolbar?.();
    }

    updateAutomationButtons();
}

// ---------------------------------------------------------------------------
// Command tab rendering
// ---------------------------------------------------------------------------

function statBlock(label, valueHtml, note) {
    return `
        <div class="flex-1 min-w-[200px] rounded-lg p-3" style="background:rgba(177,140,255,0.10); border:2px solid ${VIOLET_DIM};">
            <div class="text-[11px] uppercase tracking-widest text-gray-400">${label}</div>
            <div class="text-lg font-bold leading-tight" style="color:${VIOLET}">${valueHtml}</div>
            <div class="text-xs text-gray-400 mt-0.5 leading-snug">${note}</div>
        </div>`;
}

function renderCommandStats() {
    const container = document.getElementById('command-stats');
    if (!container) return;
    const command = ensureCommandState();

    let activeValue = '<span class="text-gray-400">No active character</span>';
    let activeNote = 'Create a character to begin the next tour';
    if (gameState.character) {
        const { total, breakdown } = calculateCommandMarks(gameState.character);
        activeValue = `${gameState.character.name} · ${total}🎖️`;
        // Marks/day makes the promotion peak legible: when the rate stops
        // climbing tour-over-tour, it's time to promote
        const tourDays = (Date.now() - (gameState.character.createdAt || 0)) / 86400000;
        const rate = gameState.character.createdAt && tourDays >= 1 / 24 ? `${(total / tourDays).toFixed(1)}🎖️/day this tour` : '';
        activeNote = `<div class="mt-1">${rate}</div>`;
    }

    container.innerHTML =
        statBlock('Command Marks', (command.marks || 0).toLocaleString(), 'Influence carried up-well by your promoted officers') +
        statBlock('Current Tour', `Tour ${command.tour}`, `${command.legends.length} Legend${command.legends.length === 1 ? '' : 's'} enshrined · counted across all factions`) +
        statBlock('Active Character — Command Value', activeValue, activeNote);
}

function rankPips(owned, max) {
    let pips = '';
    for (let i = 0; i < max; i++) {
        pips += `<span class="inline-block w-2.5 h-2.5 rounded-sm mr-1" style="border:1px solid ${VIOLET_DIM}; ${i < owned ? `background:${VIOLET};` : ''}"></span>`;
    }
    return `<span title="Rank ${owned}/${max}">${pips}</span>`;
}

function renderCommandUpgrades() {
    const container = document.getElementById('command-upgrades');
    if (!container) return;
    const command = ensureCommandState();

    container.innerHTML = Object.entries(config.commandUpgrades).map(([id, def]) => {
        const rank = getUpgradeRank(id);
        const maxed = rank >= def.costs.length;
        // Discounted price via the shared helper, so the card quotes exactly
        // what purchaseUpgradeCore will deduct.
        const nextCost = maxed ? null : commandUpgradeCost(id, rank);
        // Prerequisites are a different kind of "no" from price, so they get
        // their own line rather than a silently disabled button.
        const missing = maxed ? [] : requiredUpgradesMissing(def);
        const affordable = !maxed && missing.length === 0 && command.marks >= nextCost;
        const rankLabel = def.costs.length > 1 && !maxed ? `Rank ${['I', 'II', 'III'][rank] || rank + 1} — ` : '';
        const missingNames = missing.map(reqId => config.commandUpgrades?.[reqId]?.name || reqId);

        return `
            <div class="catalog-card bg-white/10 border-2 border-gray-700 rounded-lg p-3 flex flex-col gap-1.5">
                <div class="flex justify-between items-baseline gap-2">
                    <h4 class="font-bold text-[15px] m-0" style="color:${VIOLET}">${def.icon} ${def.name}</h4>
                    ${rankPips(rank, def.costs.length)}
                </div>
                <div class="catalog-card-flavor text-xs text-gray-400 italic leading-snug" title="${def.flavor}">${def.flavor}</div>
                <div class="catalog-card-effect text-[13px] leading-snug">${def.effect}${rank > 0 && !maxed ? ` <span class="text-game-success">Rank ${['I', 'II', 'III'][rank - 1] || rank} owned.</span>` : ''}</div>
                ${missingNames.length ? `<div class="text-xs text-amber-300">🔒 Requires ${missingNames.join(' and ')}.</div>` : ''}
                <div class="catalog-card-footer flex justify-between items-center mt-auto pt-2">
                    ${maxed
                        ? `<span class="text-game-success text-sm font-bold">✓ Maxed</span><span></span>`
                        : `<span class="font-bold text-sm" style="color:${VIOLET}">${rankLabel}${nextCost} 🎖️</span>
                           <button onclick="purchaseCommandUpgrade('${id}')" ${affordable ? '' : 'disabled'}
                               class="px-3 py-1.5 rounded text-xs font-bold ${affordable ? 'text-black hover:shadow-lg' : 'bg-gray-600 text-gray-800 cursor-not-allowed'}"
                               ${affordable ? `style="background:${VIOLET}"` : ''}>ACQUIRE</button>`}
                </div>
            </div>`;
    }).join('');
}

function renderCommandGallery() {
    const container = document.getElementById('command-gallery');
    if (!container) return;
    const command = ensureCommandState();

    if (!command.legends.length) {
        container.innerHTML = `
            <div class="col-span-full text-center text-gray-400 py-8 border border-dashed border-gray-600 rounded-lg">
                No Legends yet. Promote your first character to begin building the faction ranks.
            </div>`;
        return;
    }

    container.innerHTML = [...command.legends].reverse()
        .map(legend => renderLegendCard({ artifactsUnlocked: 0, ...legend },
            { accent: VIOLET, accentDim: VIOLET_DIM }))
        .join('');
}

registerSubTabs('command', {
    panes: { upgrades: 'command-pane-upgrades', legends: 'command-pane-legends' },
    // The GRID scrolls, not the pane: the blurb above it should stay put rather
    // than scroll away with the cards.
    scrollers: { upgrades: 'command-upgrades', legends: 'command-gallery' },
});

export function renderCommandTab() {
    renderCommandStats();
    renderCommandUpgrades();
    renderCommandGallery();
    updateCommandHeader();
    // Both panes are rendered above — they are hidden, not unmounted, so the
    // cost is one innerHTML each and the hidden one is correct when revealed.
    // Re-applying re-measures the scroller against whatever height the stats
    // strip ended up at.
    applySubTabs('command');
}

// ---------------------------------------------------------------------------
// Upgrade purchase (+ Regional Charter region pick)
// ---------------------------------------------------------------------------

export function purchaseCommandUpgrade(upgradeId) {
    const def = config.commandUpgrades?.[upgradeId];
    if (!def) return;

    if (def.choice === 'region') {
        openCharterPickModal();
        return;
    }

    const result = purchaseUpgradeCore(upgradeId);
    if (!result.success) {
        addNotification(purchaseFailureMessage(result.reason, result), 'error');
        return;
    }
    addNotification(`🎖️ Acquired ${def.name}${def.costs.length > 1 ? ` Rank ${['I', 'II', 'III'][result.rank - 1] || result.rank}` : ''} (-${result.cost} marks)`, 'success');
    saveGameState();
    renderCommandTab();
}

function purchaseFailureMessage(reason, result = null) {
    switch (reason) {
        case 'insufficient-marks': return 'Not enough Command Marks!';
        case 'max-rank': return 'Already at maximum rank!';
        case 'region-already-chartered': return 'That region is already chartered!';
        case 'invalid-region': return 'Pick a region to charter first.';
        case 'missing-requirement': {
            const names = (result?.requires || []).map(id => config.commandUpgrades?.[id]?.name || id);
            return `Requires ${names.join(' and ')} first.`;
        }
        default: return 'Cannot acquire this upgrade.';
    }
}

function openCharterPickModal() {
    const modal = document.getElementById('charter-pick-modal');
    const body = document.getElementById('charter-pick-body');
    if (!modal || !body) return;

    const command = ensureCommandState();
    const rank = getUpgradeRank('regional-charter');
    const def = config.commandUpgrades['regional-charter'];
    if (rank >= def.costs.length) {
        addNotification('Already at maximum rank!', 'error');
        return;
    }
    const cost = def.costs[rank];
    const chartered = new Set(command.charterRegions);
    const candidates = Object.keys(config.raidRegions).filter(r => !chartered.has(r));

    body.innerHTML = `
        <p class="text-sm text-gray-300 mb-1">Command negotiates access corridors no field team could.</p>
        <p class="text-xs text-gray-400 mb-4">Choose one region to unlock for ALL your characters — ${cost} 🎖️ (you have ${command.marks}).</p>
        <div class="flex flex-col gap-2">
            ${candidates.map(regionKey => {
                const region = config.raidRegions[regionKey];
                return `
                    <button onclick="confirmCharterPick('${regionKey}')" ${command.marks >= cost ? '' : 'disabled'}
                        class="text-left px-4 py-3 rounded-lg border-2 border-gray-600 bg-white/5 hover:border-[#b18cff] disabled:opacity-50 disabled:cursor-not-allowed">
                        <span class="font-bold text-white">${region.icon || '🗺️'} ${region.name}</span>
                        <span class="block text-xs text-gray-400">${region.theme || ''}</span>
                    </button>`;
            }).join('')}
        </div>`;
    modal.classList.remove('hidden');
}

export function confirmCharterPick(regionKey) {
    const result = purchaseUpgradeCore('regional-charter', regionKey);
    if (!result.success) {
        addNotification(purchaseFailureMessage(result.reason, result), 'error');
        return;
    }
    document.getElementById('charter-pick-modal')?.classList.add('hidden');
    addNotification(`🗺️ Regional Charter: ${config.raidRegions[regionKey]?.name || regionKey} unlocked for all characters! (-${result.cost} marks)`, 'success');
    saveGameState();
    renderCommandTab();
}

// ---------------------------------------------------------------------------
// Promote modal
// ---------------------------------------------------------------------------

export function openPromoteModal() {
    const modal = document.getElementById('promote-modal');
    const body = document.getElementById('promote-modal-body');
    const title = document.getElementById('promote-modal-title');
    if (!modal || !body || !gameState.character) return;

    const character = gameState.character;
    const command = ensureCommandState();
    title.textContent = `🎖️ Promote ${character.name}`;

    const eligible = canPromoteCore();
    if (!eligible.ok && eligible.reason !== 'no-active-character') {
        const message = eligible.reason === 'listed-on-marketplace'
            ? `<b>${character.name} is listed on the contractor marketplace.</b><br>Recall them from contract before promoting — the listing (or an active hire) must end first.`
            : `<b>${character.name} has a raid in progress.</b><br>Finish or abandon the raid before promoting.`;
        body.innerHTML = `
            <div class="border rounded-lg p-4 text-sm leading-relaxed mb-4" style="border-color:#ef4444; background:rgba(239,68,68,0.08); color:#fca5a5;">⚠️ ${message}</div>
            <div class="flex justify-end">
                <button onclick="closePromoteModal()" class="px-4 py-2 rounded border border-gray-500 text-gray-300 text-sm font-bold">Close</button>
            </div>`;
        modal.classList.remove('hidden');
        return;
    }

    const { total, breakdown } = calculateCommandMarks(character);
    const marksCfg = config.progressionScaling.commandMarks;
    const vaultShare = config.progressionScaling.vaultCarryover;
    // The post waiting up-well: promoted role + a command member's welcome,
    // both resolved from the character's title/best skill (deterministic, so
    // this preview always matches the Legend enshrined on confirm)
    const promotedFlavor = resolvePromotedFlavor(character);
    const factionInfo = config.factionSkills[character.faction] || {};
    // Promotion credit levy: credits are account-shared, so a percentage cut
    // (with a working-capital floor) replaces the old flat reset
    const levy = config.progressionScaling.promotionCreditLevy || { keepFraction: 0.25, floor: 1000 };
    const creditsNow = gameState.player.credits || 0;
    // Promotion SELLS relics and consumables before the levy is taken, so the
    // levy lands on a bigger number than the header shows. Dry-run the same
    // function the promotion uses — passing no vault/consignment makes it a pure
    // valuation — or this modal promises less than the player actually keeps.
    const { credits: liquidation } = disburseInventory(character.inventory, { vaultShare });
    const creditsAtLevy = creditsNow + liquidation;
    const creditsKept = Math.max(levy.floor, Math.floor(creditsAtLevy * levy.keepFraction));

    body.innerHTML = `
        <div class="text-s text-gray-100 mb-1">End of Tour ${command.tour} — Promotion to Faction Command</div>
        <!-- The mechanic has always been irreversible; nothing said so. Also the
             only place L1/L2 states that Command is PLANETSIDE, which is what
             makes a promoted officer unable to go home and what L3's founding
             needs to read as a diaspora rather than an incorporation.
             docs/story/StoryAlignment.md §Economic geography. -->
        <div class="text-xs text-gray-400 mb-3 leading-relaxed">
            Promotion is one-way. Enough transit dilation has accumulated that ${character.name} cannot return to the field — or home. Faction Command is planetside, and it is where the crossing leaves them.
        </div>
        ${promotedFlavor.role ? `
        <div class="border rounded-lg p-3 mb-4" style="border-color:${VIOLET_DIM}; background:rgba(177,140,255,0.04);">
            <div class="uppercase tracking-widest text-[10px] font-bold mb-1" style="color:${VIOLET}">New Post — ${factionInfo.icon || ''} ${factionInfo.name || character.faction} Command</div>
            <div class="text-sm font-bold text-white mb-2">${character.name}, ${promotedFlavor.role}</div>
            ${promotedFlavor.quote ? `
            <div class="text-xs italic leading-relaxed" style="color:#a78bda;">
                ${promotedFlavor.quote}
                <div class="not-italic text-right text-[10px] mt-1 text-gray-500">— ${factionInfo.name || character.faction} Command</div>
            </div>` : ''}
        </div>` : ''}
        <div class="text-xs italic border-l-2 pl-3 py-1 mb-4 leading-relaxed" style="border-color:${VIOLET_DIM}; color:#a78bda; background:rgba(177,140,255,0.06);">
            “Field clearance expires with accumulated temporal desync. The Relay is waiting, Commander. Home is… further than it was.”
        </div>
        <table class="w-full text-sm mb-4" style="font-variant-numeric: tabular-nums;">
            <tr class="border-b border-gray-700"><td class="py-1">Faction tech gear</td><td class="text-right text-gray-400">${breakdown.techGear.count} × ${marksCfg.perTechGear}</td><td class="text-right font-bold w-20" style="color:${VIOLET}">${breakdown.techGear.marks}</td></tr>
            <tr class="border-b border-gray-700"><td class="py-1" title="One-time milestone per artifact design unlocked with its blueprint">Artifact designs unlocked</td><td class="text-right text-gray-400">${breakdown.artifactUnlocks.count} × ${marksCfg.perArtifactUnlock}</td><td class="text-right font-bold" style="color:${VIOLET}">${breakdown.artifactUnlocks.marks}</td></tr>
            <tr class="border-b border-gray-700"><td class="py-1" title="Each distinct artifact this character crafted from unlocked designs">Artifacts crafted</td><td class="text-right text-gray-400">${breakdown.artifacts.count} × ${marksCfg.perArtifactCraft}</td><td class="text-right font-bold" style="color:${VIOLET}">${breakdown.artifacts.marks}</td></tr>
            <tr class="border-b border-gray-700"><td class="py-1">Tier-3 victories</td><td class="text-right text-gray-400">${breakdown.t3Wins.count} ÷ ${marksCfg.t3WinsDivisor}</td><td class="text-right font-bold" style="color:${VIOLET}">${breakdown.t3Wins.marks}</td></tr>
            <tr><td class="py-1">Level</td><td class="text-right text-gray-400">${breakdown.level.count} × ${marksCfg.perLevel}</td><td class="text-right font-bold" style="color:${VIOLET}">${breakdown.level.marks}</td></tr>
            <tr class="border-t-2" style="border-color:${VIOLET_DIM}"><td class="py-1 font-bold text-white">Command Marks</td><td></td><td class="text-right font-bold text-game-gold text-base">🎖️${total}</td></tr>
        </table>

        <div class="grid grid-cols-2 gap-3 mb-4 text-xs leading-relaxed">
            <div class="border border-gray-700 rounded-lg p-3">
                <div class="uppercase tracking-widest text-[10px] font-bold mb-1 text-game-success">✓ Persists</div>
                <ul class="list-disc pl-4 text-gray-400">
                    <li>Command Marks &amp; all upgrades</li>
                    <li>Legends gallery entry</li>
                    <li>Chartered regions</li>
                    <li>Your other characters</li>
                    <li>Artifact blueprints (filed with Command)</li>
                    ${vaultShare > 0
                        ? `<li>Materials: ${Math.round(vaultShare * 100)}% to your next recruit, the rest consigned to your next Syndicate</li>`
                        : ""}
                </ul>
            </div>
            <div class="border border-gray-700 rounded-lg p-3">
                <div class="uppercase tracking-widest text-[10px] font-bold mb-1 text-game-danger">✗ Lost</div>
                <ul class="list-disc pl-4 text-gray-400">
                    <li>${character.name} (leaves your roster)</li>
                    <li>Equipped gear, and relics &amp; consumables (sold off first — the credits below include them)</li>
                    <li title="Your credit line was issued against a tour. When the tour ends the balance underwrites the next roster; what is left is your working float."><b>Tour accounts close</b> — Command levies ${Math.round((1 - levy.keepFraction) * 100)}% of credits (${creditsAtLevy.toLocaleString()}${liquidation > 0 ? ` incl. ${liquidation.toLocaleString()} from the sale` : ""} → ${creditsKept.toLocaleString()})</li>
                </ul>
            </div>
        </div>

        <input type="text" id="promote-confirm-name" autocomplete="off" placeholder='Type "${character.name}" to confirm'
            class="w-full px-4 py-2 bg-white/5 border-2 border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none text-sm mb-4" style="caret-color:${VIOLET}">

        <div class="flex justify-end gap-3">
            <button onclick="closePromoteModal()" class="px-4 py-2 rounded border border-gray-500 text-gray-300 text-sm font-bold">Cancel</button>
            <button id="promote-confirm-btn" onclick="confirmPromotion()" disabled
                class="px-5 py-2 rounded text-sm font-bold text-black disabled:bg-gray-600 disabled:text-gray-800 disabled:cursor-not-allowed" style="background:${VIOLET}">
                Promote to Command
            </button>
        </div>`;

    const input = document.getElementById('promote-confirm-name');
    const confirmBtn = document.getElementById('promote-confirm-btn');
    input.addEventListener('input', () => {
        confirmBtn.disabled = input.value.trim().toLowerCase() !== character.name.trim().toLowerCase();
    });
    input.addEventListener('focus', () => { input.style.borderColor = VIOLET; });

    modal.classList.remove('hidden');
    // Focus without scrolling: the confirm input sits at the bottom, and a
    // scroll-on-focus would jump the (now scrollable) body past the marks
    // breakdown at the top. Keep the body pinned to the top on open.
    input.focus({ preventScroll: true });
    if (body) body.scrollTop = 0;
}

export function closePromoteModal() {
    document.getElementById('promote-modal')?.classList.add('hidden');
}

export function confirmPromotion() {
    const character = gameState.character;
    if (!character) return;
    const multiplayerId = character.multiplayerId;

    const result = promoteCharacterCore();
    if (!result.success) {
        addNotification(purchaseFailureMessage(result.reason, result), 'error');
        closePromoteModal();
        return;
    }

    // Server-side v1 reuses the delete path (switches to a status flag when Old Guard ships)
    if (multiplayerId && window.multiplayer?.deleteOwnCharacter) {
        window.multiplayer.deleteOwnCharacter(multiplayerId);
    }

    // Fresh outpost for the next tour
    gameState.selectedFaction = null;
    gameState.contractors = [];
    gameState.currentRaid.hiredContractors = [];
    gameState.currentRaid.reservedContractors = [];
    generateContractors();

    closePromoteModal();
    saveGameState();

    addNotification(`⏳ Tour ${result.legend.tour} complete — ${result.legend.name} promoted to Command · +${result.marks.total} 🎖️`, 'success');

    // The active slot is free: show the character screen (creator or selector)
    updateTabVisibility();
    updateUI();
    updateCommandHeader();
    if (window.showCharacterScreenAfterPromotion) {
        window.showCharacterScreenAfterPromotion();
    }
    renderCommandTab();
}

// ---------------------------------------------------------------------------
// Automation upgrades: Auto-Scout + Foreman's Ledger (raid planning),
// Auto-Raid Protocol (the raid loop), Auto-Fabricator (the workshop)
// ---------------------------------------------------------------------------

/**
 * Show/hide the automation buttons based on owned upgrades.
 *
 * Called from updateCommandHeader (5s interval). The raid-planning buttons live
 * in the raid grid header, so they only appear once a grid exists anyway -
 * Auto-Fabricator's does NOT: it sits in the character subtab bar, because it
 * crafts whenever the player is idle rather than only around a raid.
 */
function updateAutomationButtons() {
    // The `-arrow` companions these used to toggle no longer exist in the
    // markup; optional chaining made their absence silent.
    document.getElementById('auto-scout-btn')?.classList.toggle('hidden', getUpgradeRank('auto-scout') <= 0);
    document.getElementById('auto-reserve-btn')?.classList.toggle('hidden', getUpgradeRank('foremans-ledger') <= 0);
    updateAutoRaidButton();
    updateAutoFabricatorButton();
}

/**
 * Auto-Raid is a toggle, so its button carries STATE, not just an action: lit
 * while running, dim while idle, with a status line saying what it is doing or
 * why it is not. An automation that silently does nothing is indistinguishable
 * from one that is broken.
 */
function updateAutoRaidButton() {
    const btn = document.getElementById('auto-raid-btn');
    const status = document.getElementById('auto-raid-status');
    if (!btn) return;

    const owned = isAutoRaidOwned();
    btn.classList.toggle('hidden', !owned);
    status?.classList.toggle('hidden', !owned);
    if (!owned) return;

    const on = isAutoRaidEnabled();
    btn.style.background = on ? '#4ade80' : '';
    btn.style.color = on ? '#000' : '';
    btn.title = on ? 'Auto-Raid Protocol: running — click to stop' : 'Auto-Raid Protocol: click to run raids hands-off';

    if (!status) return;
    if (!on) {
        status.textContent = 'Auto-Raid off';
        status.style.color = '#9ca3af';
        return;
    }
    const blocked = autoRaidBlockedReason();
    const waitingForEnergy = isWaitingForEnergy();
    status.textContent = blocked ? `⏸ ${blocked.text}`
        : waitingForEnergy ? `⏸ Waiting for ⚡${plannedEnergyCost()}`
        : '▶ Auto-Raid running';
    status.style.color = (blocked || waitingForEnergy) ? '#fbbf24' : '#4ade80';
}

export function toggleAutoRaid() {
    if (!isAutoRaidOwned()) return;
    const nowOn = setAutoRaidEnabled(!isAutoRaidEnabled());
    updateAutoRaidButton();
    if (nowOn) {
        const blocked = autoRaidBlockedReason();
        addNotification(blocked ? `🤖 Auto-Raid armed — ${blocked.text}` : '🤖 Auto-Raid running', blocked ? 'warning' : 'success');
    } else {
        addNotification('🤖 Auto-Raid stopped', 'info');
    }
}

/**
 * Auto-Fabricator's button: a toggle, so it carries STATE rather than an action.
 *
 * It sits in the CHARACTER subtab bar, not the raid grid header where Auto-Raid
 * lives, because it is not raid-scoped — it crafts whenever the player is idle.
 * That bar has no room for a status line beside it, so the LABEL is the status
 * and the tooltip carries the detail. An automation that silently does nothing
 * is indistinguishable from one that is broken, and "Off" vs "⏸" is the
 * difference between "you turned this off" and "it cannot run right now".
 */
const FAB_BUTTON_STATES = {
    off: { text: 'Auto-Craft Off', colour: '#b18cff', title: 'Auto-Fabricator: click to craft hands-off between raids' },
    working: { text: 'Auto-Craft On', colour: '#4ade80', title: 'Auto-Fabricator: crafting between raids — click to stop' },
    // Not a failure, and the most common state early on: everything craftable is
    // already built, or the materials and credits are not there yet.
    nothing: { text: 'Auto-Craft Idle', colour: '#9ca3af', title: 'Auto-Fabricator: on, but nothing it can build right now — no blueprint to unlock, no complete recipe, nothing affordable' },
    blocked: { text: 'Auto-Craft ⏸', colour: '#fbbf24', title: null },
};

function updateAutoFabricatorButton() {
    const btn = document.getElementById('auto-fab-btn');
    if (!btn) return;

    // One source of truth: the module that runs the automation decides what
    // state it is in, and this only paints it.
    const { state, reason } = autoFabricatorStatus();
    btn.classList.toggle('hidden', state === 'unowned');
    if (state === 'unowned') return;

    const style = FAB_BUTTON_STATES[state];
    btn.textContent = style.text;
    btn.style.borderColor = style.colour;
    btn.style.background = `${style.colour}22`;
    btn.style.color = style.colour;
    btn.title = style.title || `Auto-Fabricator paused — ${reason}`;
}

export function toggleAutoFabricator() {
    if (!isAutoFabricatorOwned()) return;
    const nowOn = setAutoFabricatorEnabled(!isAutoFabricatorEnabled());
    updateAutoFabricatorButton();
    if (nowOn) {
        const blocked = autoFabricatorBlockedReason();
        addNotification(blocked ? `⚙️ Auto-Fabricator armed — ${blocked.text}` : '⚙️ Auto-Fabricator running', blocked ? 'warning' : 'success');
    } else {
        addNotification('⚙️ Auto-Fabricator stopped', 'info');
    }
}

/**
 * Auto-Scout: chart the best start->exit path for the character's skills
 * (the simulator's battle-tested BFS, shared via raidPath-core.js). Rooms
 * that drop the player's selected filter items dominate the scoring, exactly
 * like the simulator's loot-targeted player model.
 */
export function autoScoutPath() {
    const grid = gameState.currentRaid.currentRaidGrid;
    if (!grid || !gameState.character || gameState.currentRaid.raidInProgress) return;

    // Lights Out. Told, not silently ignored: a button that does nothing when
    // pressed is indistinguishable from a broken one.
    if (blackoutModifier('noAutoScout', false)) {
        addNotification('🌑 Lights Out: no recon on this contract — chart it yourself.', 'warning');
        return;
    }

    const wantedCells = getHighlightedWantedCells();
    // Weight encounter rooms by whether this character can pass them: a gate
    // fires 2-4 skill events against a higher skill bar than a hazard's 1-2,
    // so routing through gates the player is under-skilled for is what turns a
    // scouted path into a wipe.
    const tierData = config.raidTierData[grid.tier] || {};
    const skillTargets = { gate: tierData.gateSkillLevel, hazard: tierData.hazardSkillLevel };
    let path = findPathBFS(grid, grid.startPos, grid.endPos, gameState.character.skills || {}, wantedCells, skillTargets);
    if (!path.length) {
        addNotification('🛰️ Auto-Scout could not chart a route to the exit', 'error');
        return;
    }
    // Grab any water/campsite that sit on a free (zero-encounter) detour off the
    // charted route - free fishing/healing at no path-length cost.
    path = insertFreeSpecialDetours(path, grid);

    gameState.currentRaid.selectedPath = path;
    updateGridDisplay();
    updatePathDisplay();
    updateCurrentRaidInfo();
    updateStartRaidButton();
    saveGameState();
    const encounters = path.filter(s => s.room?.type === 'gate' || s.room?.type === 'hazard').length;
    const targeted = wantedCells ? path.filter(s => wantedCells.has(`${s.row},${s.col}`)).length : 0;
    addNotification(`🛰️ Auto-Scout charted a ${path.length}-room route (${encounters} encounters${targeted ? `, ${targeted} rooms drop your marked items` : ''})`, 'success');
}

/**
 * Foreman's Ledger: reserve the best available contractors for the planned
 * path (reuses the modal reserve flow, so team limits/server rules apply).
 */
export async function autoReserveContractors() {
    const path = gameState.currentRaid.selectedPath;
    if (!path || path.length < 2) {
        addNotification('📒 Plan a path first — the Foreman staffs for the route', 'warning');
        return;
    }
    if (gameState.currentRaid.reservedContractors.length >= 2) {
        addNotification('📒 Team is already full', 'warning');
        return;
    }

    const available = (gameState.contractors || []).filter(c => c.isAlive !== false && !isContractorReserved(c.id));
    const best = selectBestContractorsForPathCore(available, path, gameState.character?.skills || {});
    if (!best.length) {
        addNotification('📒 No suitable contractors available for this route', 'warning');
        return;
    }

    let hired = 0;
    for (const contractor of best) {
        if (gameState.currentRaid.reservedContractors.length >= 2) break;
        await reserveContractorFromModal(contractor.id);
        hired++;
    }
    if (hired > 0) {
        addNotification(`📒 Foreman's Ledger reserved ${hired} contractor${hired === 1 ? '' : 's'} for the route`, 'success');
    }
}

// ---------------------------------------------------------------------------
// Window exposure (onclick-string pattern used across the codebase)
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
    window.openPromoteModal = openPromoteModal;
    window.closePromoteModal = closePromoteModal;
    window.confirmPromotion = confirmPromotion;
    window.purchaseCommandUpgrade = purchaseCommandUpgrade;
    window.confirmCharterPick = confirmCharterPick;
    window.autoScoutPath = autoScoutPath;
    window.autoReserveContractors = autoReserveContractors;
    window.toggleAutoRaid = toggleAutoRaid;
    window.toggleAutoFabricator = toggleAutoFabricator;

    document.addEventListener('DOMContentLoaded', () => {
        document.getElementById('promote-modal-close')?.addEventListener('click', closePromoteModal);
        document.getElementById('charter-pick-close')?.addEventListener('click', () => {
            document.getElementById('charter-pick-modal')?.classList.add('hidden');
        });
    });
}
